import { TbFileCheck } from 'react-icons/tb'

import { CRM_CONTRACT_FILE_EXTENSIONS, CRM_FILE_MAX_SIZE_MB } from '@/Containers/CrmSection/Components/Contracts/CrmContract'
import { exportToExcelButton } from '@/Containers/CrmSection/Components/Export'
import { RichTextInput } from '@/Ship/Components/RichText/RichTextInput'
import { renderRichTextView } from '@/Ship/Components/RichText/RichTextView'
import { StatusDictionarySelect } from '@/Ship/Components/FormFields/StatusDictionarySelect'
import { attachUploadedFiles } from '@/Ship/Helpers/attachUploadedFiles'
import { renderDateTimeView, renderDateView, renderUsersView } from '@/Ship/Helpers/fieldViews'

import { addApplicationButtons } from '../Components/ApplicationButtons'
import { ApplicationLink, ApplicationStatus } from '../Components/ApplicationCells'
import { addApplicationKanbanMode } from '../Components/ApplicationKanban'
import { APPLICATION_FIELD_LABELS as L } from '../Contracts/ApplicationFieldLabels'
import {
    APPLICATION_DRAWER_WIDTH,
    APPLICATION_FILE_UPLOAD_URL,
    APPLICATION_LOSS_REASON_RESOURCE,
    APPLICATION_PAGE_SIZE,
    APPLICATION_REST_URL,
    APPLICATION_STATUS_CODES,
    APPLICATION_STATUS_RESOURCE,
} from '../Contracts/IApplication'
import { ApplicationFormTemplate } from '../Templates/ApplicationFormTemplate'

import type { ApplicationRecord } from '../Contracts/IApplication'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'
import type { IResourceConfig } from '@/Core/Contracts/UI/Resource'
import type { ReactNode } from 'react'

const sourceOf = (row: unknown): ApplicationRecord => {
    const item = (row ?? {}) as ApplicationRecord & { _original?: ApplicationRecord }

    return item._original ?? item
}

/**
 * Заголовки й ширини колонок — легасі (`ApplicationCrud.jsx:102-117`), з
 * переносами рядків, як бачив користувач.
 */
const COLUMN_TITLES: Record<string, { title: ReactNode; width: number }> = {
    id: { title: 'Ід', width: 70 },
    client: { title: 'Клієнт', width: 180 },
    status: { title: 'Статус', width: 155 },
    loadingCountries: {
        title: (
            <>
                Країни
                <br />
                завант.
            </>
        ),
        width: 115,
    },
    dateLoading: {
        title: (
            <>
                Дата
                <br />
                завант.
            </>
        ),
        width: 110,
    },
    unloadingCountries: {
        title: (
            <>
                Країни
                <br />
                розвант.
            </>
        ),
        width: 115,
    },
    typesCars: { title: 'Тип а/м', width: 115 },
    manager: {
        title: (
            <>
                Менеджер
                <br />
                клієнт
            </>
        ),
        width: 120,
    },
    responsible: {
        title: (
            <>
                Менеджер
                <br />
                логіст
            </>
        ),
        width: 120,
    },
    carrier: { title: 'Перевізник', width: 160 },
    numberVehicle: { title: 'Держ. Номер', width: 135 },
    dateUnloading: {
        title: (
            <>
                Дата
                <br />
                розвант.
            </>
        ),
        width: 110,
    },
    createdAt: { title: 'Створено', width: 90 },
    statusClient: { title: 'Статус', width: 115 },
}

/**
 * Колонки, які ядро само не намалює як треба (`SPEC-021` §8.1):
 *
 *  - **«Статус»** — бейдж кольору довідника і бейдж «Кількість пропозицій»
 *    (на «Рейсах» — без нього, `showOffers`);
 *  - **«Клієнт»**, **«Перевізник»** — посилання на картку (легасі
 *    `ApplicationCrud.jsx:31-60`).
 */
export const applicationColumnRenderers =
    (showOffers: boolean) =>
    ({ columns }: { columns: Array<Record<string, unknown>> }) => {
        columns.forEach((column) => {
            const key = String(column['key'] ?? '')
            const legacy = COLUMN_TITLES[key]

            if (legacy) {
                column['title'] = legacy.title
                column['width'] = legacy.width
            }

            if (key === 'status') {
                column['render'] = (_: unknown, row: unknown): ReactNode => {
                    const record = sourceOf(row)

                    return <ApplicationStatus status={record.status} countOffers={record.countOffers} showOffers={showOffers} />
                }
            }

            if (key === 'client' || key === 'carrier') {
                column['render'] = (_: unknown, row: unknown): ReactNode => (
                    <ApplicationLink kind={key} record={sourceOf(row)[key]} />
                )
            }
        })

        return columns
    }

/** Довідник-select (`SPEC-007`): кілька рядків — і у фільтрі «будь-який з обраних». */
const dictionary = (label: string, resource: string, isMultiple = false): IFieldConfig => ({
    label,
    type: 'select',
    resource,
    ...(isMultiple ? { isMultiple: true, isFilterMultiple: true } : {}),
})

/** Лише перегляд: ставить сервер (резолвер, переходи, аудит). */
const readOnly = (field: IFieldConfig): IFieldConfig => ({
    ...field,
    isEditable: false,
    inputProps: { ...(field.inputProps ?? {}), disabled: true },
})

const user = (label: string): IFieldConfig => ({
    label,
    type: 'select',
    resource: 'Setting.User',
    isFilterMultiple: true,
    view: renderUsersView,
})

const text = (label: string, maxLength = 255): IFieldConfig => ({ label, inputProps: { maxLength } })

const money = (label: string): IFieldConfig => ({
    label,
    type: 'number',
    defaultValue: 0,
    inputProps: { min: 0, precision: 2, style: { width: '100%' } },
})

const currency = (label: string): IFieldConfig => ({
    label,
    type: 'select',
    resource: 'Setting.ListCurrency',
    responseLabel: (item: { code?: unknown; title?: unknown }) =>
        typeof item?.code === 'string' ? item.code : String(item?.title ?? ''),
})

/**
 * Поля, яких бек у тілі не приймає (`application:create` / `:update`, ДЗ-04):
 * статус, назву, причину програшу, проблему ставлять резолвер і переходи,
 * дати й авторів — аудит, прибуток — калькулятор. `statusClient` міняє
 * модалка «Оновити статус», а не форма.
 */
const READ_ONLY_KEYS = [
    'id',
    'title',
    'status',
    'externalCode',
    'amountProfitUAH',
    'profitRateDate',
    'dateAdditionResponsible',
    'lossReason',
    'lossComment',
    'isProblem',
    'problem',
    'isSolvedProblem',
    'solvedProblem',
    'statusClient',
    'dateUpdateStatusClient',
    'isPayments',
    'isSentEmail',
    'isRemoved',
    'countOffers',
    'clientContract',
    'carrierContract',
    'createdAt',
    'createdBy',
    'updatedAt',
    'updatedBy',
] as const

const prepareApplicationPayload = ({
    values,
    formContext,
}: {
    values: Record<string, unknown>
    formContext?: { values?: Record<string, unknown> }
}): void => {
    READ_ONLY_KEYS.forEach((key) => delete values[key])

    // Джерело заявки ставиться лише при створенні (ДЗ-04)
    if (typeof formContext?.values?.['@id'] === 'string') {
        delete values['request']
        delete values['tender']
    }

    // Порожні вартості — 0: бек тримає NOT NULL (default 0)
    for (const key of ['costClient', 'costCarrier', 'costToBorder']) {
        const value = values[key]

        if (value === null || value === undefined || value === '') values[key] = 0
    }
}

/** Поля заявки (`SPEC-021` §4.1) — спільні для «Заявок» і «Рейсів». */
export const APPLICATION_FIELDS: Record<string, IFieldConfig> = {
    id: { label: L.id, type: 'number', isEditable: false },
    title: readOnly({ label: L.title }),
    // `inputProps.disabled` вимикає й ядрову колонку (вартовий колонки статусу)
    status: {
        label: L.status,
        type: 'status',
        statusResource: APPLICATION_STATUS_RESOURCE,
        isEditable: false,
        inputProps: { disabled: true },
    },
    client: { label: L.client, type: 'select', resource: 'Crm.Client', isRequired: true },
    isConfirmedClient: { label: L.isConfirmedClient, type: 'checkbox' },
    loadingCountries: { ...dictionary(L.loadingCountries, 'Setting.ListCountry', true), isRequired: true },
    loadingAddresses: { label: L.loadingAddresses, isMultiple: true, isRequired: true, inputProps: { maxLength: 255 } },
    unloadingCountries: { ...dictionary(L.unloadingCountries, 'Setting.ListCountry', true), isRequired: true },
    unloadingAddresses: { label: L.unloadingAddresses, isMultiple: true, isRequired: true, inputProps: { maxLength: 255 } },
    cargo: { label: L.cargo, type: 'textarea', isRequired: true, inputProps: { maxLength: 4096 } },
    typesCars: { ...dictionary(L.typesCars, 'Setting.ListCarType', true), isRequired: true },
    dateLoading: { label: L.dateLoading, type: 'date', isRequired: true, view: renderDateView },
    // Опис — HTML tiptap з таблицями й роздільником (Q2.8, пресет `description`)
    description: {
        label: L.description,
        input: RichTextInput,
        view: renderRichTextView,
        inputProps: { toolbar: 'description' },
    },
    planneDateUnloading: { label: L.planneDateUnloading, type: 'date', view: renderDateView },
    forwarder1: dictionary(L.forwarder1, 'Setting.ListLegalEntity'),
    costClient: money(L.costClient),
    costClientCurrency: currency(L.costClientCurrency),
    placeCountryDeparture: text(L.placeCountryDeparture),
    personResponsibleLoading: text(L.personResponsibleLoading),
    placeDestination: text(L.placeDestination),
    personResponsibleUnloading: text(L.personResponsibleUnloading),
    costCargoInvoice: text(L.costCargoInvoice),
    dateUnloading: { label: L.dateUnloading, type: 'date', view: renderDateView },
    // Двокроково й у формі створення (G-05): крок 1 — `application_files`,
    // крок 2 — `fileUuids` у POST/PATCH
    files: {
        label: L.files,
        type: 'file',
        isMultiple: true,
        uploadUrl: APPLICATION_FILE_UPLOAD_URL,
        uploadAccept: CRM_CONTRACT_FILE_EXTENSIONS,
        uploadMaxSizeMb: CRM_FILE_MAX_SIZE_MB,
        showViewIcon: false,
        showEditIcon: false,
    },
    amountProfitUAH: readOnly({ label: L.amountProfitUAH, type: 'number' }),
    manager: { ...user(L.manager), isRequired: true },
    responsible: user(L.responsible),
    dateAdditionResponsible: readOnly({ label: L.dateAdditionResponsible, type: 'datetime', view: renderDateTimeView }),
    forwarder2: dictionary(L.forwarder2, 'Setting.ListLegalEntity'),
    carrier: { label: L.carrier, type: 'select', resource: 'Crm.Carrier' },
    numberVehicle: text(L.numberVehicle),
    driver: text(L.driver),
    borderCrossing: dictionary(L.borderCrossing, 'Setting.ListBorderCrossing'),
    costToBorder: money(L.costToBorder),
    costToBorderCurrency: currency(L.costToBorderCurrency),
    typeNumberVehicles: text(L.typeNumberVehicles),
    method: text(L.method),
    costCarrier: money(L.costCarrier),
    costCarrierCurrency: currency(L.costCarrierCurrency),
    paymentTerm: { label: L.paymentTerm, type: 'number', inputProps: { min: 0, precision: 0, style: { width: '100%' } } },
    dateDeliveryCustoms: { label: L.dateDeliveryCustoms, type: 'datetime', view: renderDateTimeView },
    consignor: text(L.consignor),
    consignee: text(L.consignee),
    quantityWeightCargo: { label: L.quantityWeightCargo, type: 'textarea', inputProps: { maxLength: 4096 } },
    // Обчислюються беком на кожен показ картки (останній договір сторони з її
    // експедитором, `ApplicationContractsResolver`) — лише перегляд, у списку немає
    clientContract: readOnly({ label: L.clientContract, type: 'select', resource: 'Crm.ClientContract' }),
    carrierContract: readOnly({ label: L.carrierContract, type: 'select', resource: 'Crm.CarrierContract' }),
    request: readOnly({ label: L.request, type: 'select', resource: 'Transportation.Request' }),
    tender: readOnly({ label: L.tender, type: 'select', resource: 'Transportation.Tender' }),
    lossReason: readOnly(dictionary(L.lossReason, APPLICATION_LOSS_REASON_RESOURCE)),
    lossComment: readOnly({ label: L.lossComment, type: 'textarea' }),
    problem: readOnly({ label: L.problem, type: 'textarea' }),
    solvedProblem: readOnly({ label: L.solvedProblem, type: 'textarea' }),
    statusClient: readOnly(text(L.statusClient)),
    dateUpdateStatusClient: readOnly({ label: L.dateUpdateStatusClient, type: 'datetime', view: renderDateTimeView }),
    createdAt: readOnly({ label: L.createdAt, type: 'datetime', view: renderDateTimeView }),
    createdBy: readOnly({ label: L.createdBy, type: 'select', resource: 'Setting.User', view: renderUsersView }),
    updatedAt: readOnly({ label: L.updatedAt, type: 'datetime', view: renderDateTimeView }),
    updatedBy: readOnly({ label: L.updatedBy, type: 'select', resource: 'Setting.User', view: renderUsersView }),
}

/** Форма заявки — спільна для «Заявок» і «Рейсів»: файли двокроково, тіло без службових полів. */
export const APPLICATION_FORM = {
    eventHandlers: {
        // Крок 2: нові файли — `fileUuids`, саме поле — геть із тіла
        onBeforeTransformSendSaveRequest: {
            applicationFileUuids: attachUploadedFiles('files'),
        },
        onBeforeSendSaveRequest: {
            ApplicationPayload: prepareApplicationPayload,
        },
    },
}

/** Картка заявки — спільна для «Заявок», «Рейсів» і вкладок CRM. */
export const APPLICATION_DETAIL = {
    modeConfig: {
        // Кнопки дій — лише в перегляді й лише за `_permissions`
        view: { template: ApplicationFormTemplate, onButtons: addApplicationButtons },
        edit: { template: ApplicationFormTemplate },
        create: { template: ApplicationFormTemplate },
    },
}

/**
 * Заявки (`SPEC-021` §8.1): реєстр на 14 колонок і 12 фільтрів у порядку
 * легасі (`ApplicationCrud.jsx:66-78`), 25 рядків на сторінку; рядок статусів
 * над таблицею з `GET /services/applications/counters`, за замовчуванням —
 * «Новий» (Q2.9); режим «Канбан» — лише перегляд (§8.3).
 *
 * Права — лише з беку: «Додати» — колекційне `create`
 * (`ResourceCorrespondenceConfig`), «Редагувати» / «Видалити» і кнопки дій —
 * `_permissions` запису. «Експорт в Excel» на «Заявках» — колекційне
 * `export_registry` (адміністратор, Q2.6 1:1 з легасі). Ролей ресурс не питає.
 */
export const ApplicationResource: IResourceConfig = {
    title: 'Заявки',
    fields: APPLICATION_FIELDS,
    // 14 колонок у порядку легасі (`ApplicationCrud.jsx:73-78`)
    table: {
        showColumns: [
            'id',
            'client',
            'status',
            'loadingCountries',
            'dateLoading',
            'unloadingCountries',
            'typesCars',
            'manager',
            'responsible',
            'carrier',
            'numberVehicle',
            'dateUnloading',
            'createdAt',
            'statusClient',
        ],
        dataSourceNoTransform: ['status', 'client', 'carrier'],
        eventHandlers: {
            onBuildColumns: { ApplicationColumnRenderers: applicationColumnRenderers(true) },
        },
    },
    // 12 фільтрів у порядку легасі (`ApplicationCrud.jsx:66-71`)
    filter: {
        includeFields: [
            'id',
            'client',
            'status',
            'loadingCountries',
            'dateLoading',
            'unloadingCountries',
            'typesCars',
            'manager',
            'responsible',
            'carrier',
            'dateUnloading',
            'createdAt',
        ],
        // «Статус» — рядок довідника, як рядок статусів над таблицею: пише код
        fieldProps: {
            status: { input: StatusDictionarySelect },
        },
    },
    list: {
        pagination: { pageSize: APPLICATION_PAGE_SIZE },
        // «Новий» обрано за замовчуванням (Q2.9; легасі `ApplicationPage.jsx:8-13`)
        filter: { status: APPLICATION_STATUS_CODES.new },
    },
    headerPage: {
        eventHandlers: {
            onAfterInit: { AddApplicationKanbanMode: addApplicationKanbanMode() },
            onButtons: { ExportToExcel: exportToExcelButton('Заявки.xlsx', 'export_registry') },
        },
    },
    form: APPLICATION_FORM,
    detailConfig: APPLICATION_DETAIL,
    drawer: {
        width: APPLICATION_DRAWER_WIDTH,
    },
    restUrl: APPLICATION_REST_URL,
    pageUrl: '/applications',
    pageConfig: {
        title: 'Заявки',
        icon: <TbFileCheck />,
    },
    settings: {
        searchFields: ['title'],
    },
}
